import { Injectable } from '@angular/core';
import { Observable, of, map } from 'rxjs';
import { ServicecallService } from './servicecall.service';

interface CachedImageUrl {
  url: string;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class ImageUrlCacheService {

  private readonly URL_EXPIRY_MS = 2 * 60 * 60 * 1000; // 2 hours
  private readonly CACHE_KEY = 'vehicleImageCache';
  private refreshTimeout: any;

  expiredUrlList: Set<string> = new Set<string>();

  constructor(public service: ServicecallService) {}

  /**
   * Updates vehicle image urls
   * @param vehicleList 
   * @returns vehicle image urls 
   */
  updateVehicleImageUrls(vehicleList: any[]): Observable<any[]> {
    const urlToVehicleMap = new Map<string, any>();
    const urlsToFetch = new Set<string>();

    const cache = this._getCache();

    this.expiredUrlList?.forEach(element => {
      if(cache[element]) {
        delete cache[element];
      }
    });
    this._storeCache(cache);
    this.expiredUrlList.clear();

    // Prepare fetch list and assign cached values where valid
    vehicleList.forEach((vehicle) => {
      if (vehicle?.image) {
        const baseUrl = vehicle?.image;
        const cached = this._getFromCache(cache, baseUrl);
        if (cached) {
          vehicle.image = cached;
        } else {
          urlsToFetch?.add(baseUrl);
          urlToVehicleMap?.set(baseUrl, vehicle);
        }
      }
    });

    if (Array.from(urlsToFetch)?.length === 0) {
      return of(vehicleList);
    }

    return this._getPresignedUrlsFromAPI(Array.from(urlsToFetch))?.pipe(
      map((response) => {
        const updatedCache = { ...cache };
        response?.urls?.forEach((item: { url: string, presignedUrl: string }) => {
          const baseUrl = item?.url;
          const presignedUrl = item?.presignedUrl;

          if (baseUrl && presignedUrl) {
            updatedCache[baseUrl] = {
              url: presignedUrl,
              expiresAt: Date.now() + this.URL_EXPIRY_MS
            };

            const vehicle = urlToVehicleMap?.get(baseUrl);
            if (vehicle) {
              vehicle.image = presignedUrl;
            }
          }
        });

        this._storeCache(updatedCache);
        return vehicleList;
      })
    );
  }

  /**
   * Schedules batch refresh
   * @param callback 
   * @param [delay] 
   */
  scheduleBatchRefresh(callback: () => void, delay: number = 800) {
    clearTimeout(this.refreshTimeout);
    this.refreshTimeout = setTimeout(() => {
      if (this.expiredUrlList.size > 0) {
        callback();
      }
    }, delay);
  }

  /**
   * Gets cache
   * @returns cache 
   */
  private _getCache(): { [url: string]: CachedImageUrl } {
    const raw = sessionStorage?.getItem(this.CACHE_KEY);
    try {
      return raw ? JSON?.parse(raw) : {};
    } catch (error) {
      sessionStorage?.removeItem(this.CACHE_KEY);
      return {};
    }
  }

  /**
   * Stores cache
   * @param cache 
   */
  private _storeCache(cache: { [url: string]: CachedImageUrl }): void {
    sessionStorage.setItem(this.CACHE_KEY, JSON?.stringify(cache));
  }

  /**
   * Gets from cache using original URL
   * @param cache 
   * @param baseUrl 
   * @returns presigned url if valid
   */
  private _getFromCache(cache: { [url: string]: CachedImageUrl }, baseUrl: string): string | null {
    const entry = cache[baseUrl];
    if (entry && Date.now() < entry?.expiresAt) {
      return entry?.url;
    }
    return null;
  }

  /**
   * Gets presigned urls from api
   * @param urls 
   * @returns presigned urls from api 
   */
  private _getPresignedUrlsFromAPI(urls: string[]): Observable<any> {
    const payload = { urls };
    return this.service?.getPresignedUrls(payload);
  }

  /**
   * Sets expired url list
   * @param baseUrl 
   */
  setExpiredUrlList(baseUrl: string) {
    this.expiredUrlList?.add(baseUrl);
  }
}